import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import type { Area, GeoCandidate, GeoCorrection, GeoReviewItem, GeoStatus } from '../api/types';
import { EmptyState } from '../components/EmptyState';
import { ErrorMessage } from '../components/ErrorMessage';
import { GeoStatusBadge } from '../components/GeoStatusBadge';
import { PageLoader } from '../components/Loader';
import { MutationError } from '../components/MutationError';
import { AddressCheckBar, AddressCheckSuggestion } from '../geo/AddressChecks';
import { AddressAutocomplete } from '../places/AddressAutocomplete';
import { AddressCheck } from '../places/AddressCheck';
import { mapsSearchUrl } from '../places/maps';
import { assumedFilter, assumedLabel, geoStatusLabels, isAssumed, numberFormat, problemLabels, reviewStatuses } from '../geo/labels';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';

// Ansicht von leads.geo_details (Ergebnis der letzten automatischen Auflösung).
interface GeoDetailsView {
  message?: string;
  problems?: string[];
  candidates?: GeoCandidate[];
  resolved?: { municipality?: string; county?: string; federal_state?: string };
  normalized?: { line?: string };
  area?: { ambiguous?: boolean; matches?: { area_id: string; area_name: string }[] | null };
}

function name(item: GeoReviewItem): string {
  const person = [item.first_name, item.last_name].filter(Boolean).join(' ');
  return item.company_name ? (person ? `${item.company_name} (${person})` : item.company_name) : person || 'Ohne Namen';
}

function address(a: { street?: string | null; house_number?: string | null; postal_code?: string | null; city?: string | null } | null) {
  if (!a) return '–';
  const line1 = [a.street, a.house_number].filter(Boolean).join(' ');
  const line2 = [a.postal_code, a.city].filter(Boolean).join(' ');
  return [line1, line2].filter(Boolean).join(', ') || '–';
}

// Geo-Klärung: Leads mit widersprüchlicher oder unvollständiger Adresse, die das System nicht selbst klären kann.
// Korrekturen erfolgen hier bewusst und werden auditiert; Google-Vorschläge gelten erst nach Bestätigung.
export function GeoReviewPage({ embedded = false }: { embedded?: boolean }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const [checking, setChecking] = useState(false);
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') as GeoStatus | typeof assumedFilter | null) ?? undefined;
  // Filter setzen, ohne andere Parameter (z. B. den Reiter im Posteingang) zu verlieren.
  const setStatus = (s?: GeoStatus | typeof assumedFilter) => {
    const next = new URLSearchParams(params);
    if (s) next.set('status', s);
    else next.delete('status');
    setParams(next);
  };

  // Während die Google-Prüfung läuft, erscheinen die Vorschläge nach und nach.
  const summary = useQuery({
    queryKey: [...projectKeys.review(project.id), 'summary'],
    queryFn: ({ signal }) => papi.geoReviewSummary(signal),
    refetchInterval: (q) => (checking && (q.state.data?.address_checks?.pending ?? 0) > 0 ? 4000 : false),
  });
  const pending = summary.data?.address_checks?.pending;
  const review = useInfiniteQuery({
    queryKey: [...projectKeys.review(project.id), 'list', status],
    queryFn: ({ pageParam, signal }) => papi.geoReview({ cursor: pageParam, limit: 25, geoStatus: status ? [status] : undefined }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    refetchInterval: checking && (pending ?? 0) > 0 ? 8000 : false,
  });
  // Nach dem letzten Stapel die Liste einmal neu laden, damit alle Vorschläge sichtbar sind.
  useEffect(() => {
    if (checking && pending === 0) void queryClient.invalidateQueries({ queryKey: [...projectKeys.review(project.id), 'list'] });
  }, [checking, pending, project.id, queryClient]);
  const areas = useQuery({
    queryKey: [...projectKeys.areas(project.id), 'active'],
    queryFn: ({ signal }) => papi.projectAreas(false, signal),
    enabled: can('area.read') && can('lead.geo_override'),
  });

  const counts = summary.data?.counts ?? {};
  const total = reviewStatuses.reduce((sum, s) => sum + (counts[s] ?? 0), 0);
  const assumed = counts[assumedFilter] ?? 0;
  const items = review.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className={embedded ? 'embedded-page' : 'page'}>
      <header className="page-header">
        {!embedded && <h1>Geo-Klärung</h1>}
        {summary.data && (
          <span className="muted">
            {numberFormat.format(total)} offene Fälle
            {assumed > 0 && ` · ${numberFormat.format(assumed)} wahrscheinlich zugeordnet, noch unbestätigt`}
          </span>
        )}
      </header>
      {summary.data?.address_checks?.available && total + assumed > 0 && (
        <AddressCheckBar summary={summary.data.address_checks} checking={checking} onStarted={() => setChecking(true)} />
      )}
      <div className="chips" role="group" aria-label="Nach Problem filtern">
        <button type="button" className={!status ? 'chip active' : 'chip'} aria-pressed={!status} onClick={() => setStatus()}>
          Alle <span className="chip-count">{numberFormat.format(total)}</span>
        </button>
        {reviewStatuses.map((s) => (
          <button key={s} type="button" className={status === s ? 'chip active' : 'chip'} aria-pressed={status === s} onClick={() => setStatus(s)}>
            {geoStatusLabels[s]} <span className="chip-count">{numberFormat.format(counts[s] ?? 0)}</span>
          </button>
        ))}
        <button
          type="button"
          className={status === assumedFilter ? 'chip active' : 'chip'}
          aria-pressed={status === assumedFilter}
          onClick={() => setStatus(assumedFilter)}
        >
          {assumedLabel} <span className="chip-count">{numberFormat.format(assumed)}</span>
        </button>
      </div>
      {review.isPending ? (
        <PageLoader label="Klärungsfälle werden geladen …" />
      ) : review.isError ? (
        <ErrorMessage error={review.error} onRetry={() => void review.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title="Alles geklärt">
          {status === assumedFilter ? 'Alle wahrscheinlichen Zuordnungen sind bestätigt.' : 'Für diese Auswahl gibt es keine offenen Geo-Klärungen.'}
        </EmptyState>
      ) : (
        <ul className="review-list">
          {items.map((item) => (
            <ReviewCard key={item.id} item={item} areas={areas.data?.items ?? []} />
          ))}
        </ul>
      )}
      {review.hasNextPage && (
        <button type="button" className="btn load-more" disabled={review.isFetchingNextPage} onClick={() => void review.fetchNextPage()}>
          {review.isFetchingNextPage ? 'Lädt …' : 'Weitere laden'}
        </button>
      )}
    </div>
  );
}

function ReviewCard({ item, areas }: { item: GeoReviewItem; areas: Area[] }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const details = (item.geo_details ?? {}) as GeoDetailsView;
  const canOverride = can('lead.geo_override');
  const assumed = isAssumed(item);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    street: item.street ?? '',
    house_number: item.house_number ?? '',
    postal_code: item.postal_code ?? '',
    city: item.city ?? '',
  });
  const [countyId, setCountyId] = useState('');
  const [areaId, setAreaId] = useState('');
  const [note, setNote] = useState('');

  const save = useMutation({
    mutationFn: (extra: Partial<GeoCorrection>) => {
      const body: GeoCorrection = { note: note || undefined, ...extra };
      for (const key of ['street', 'house_number', 'postal_code', 'city'] as const) {
        if (form[key] !== (item[key] ?? '')) body[key] = form[key] || null;
      }
      if (countyId) body.county_id = countyId;
      if (areaId) body.area_id = areaId === 'none' ? null : areaId;
      return papi.correctGeo(item.id, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) });
    },
  });

  const counties = new Map<string, string>();
  for (const c of details.candidates ?? []) {
    if (c.county_id && c.county_name) counties.set(c.county_id, `${c.county_name}${c.federal_state_name ? ` (${c.federal_state_name})` : ''}`);
  }
  const submit = (extra: Partial<GeoCorrection>) => (e?: FormEvent) => {
    e?.preventDefault();
    save.mutate(extra);
  };

  return (
    <li className="card review-card">
      <div className="review-head">
        <div>
          <h2>{name(item)}</h2>
          <p className="review-address">
            <span className="muted">Erfasst:</span> {address(item.original_address ?? item)}{' '}
            <a className="maps-link" href={mapsSearchUrl(item.street, item.house_number, item.postal_code, item.city)} target="_blank" rel="noopener noreferrer">
              Karte
            </a>
          </p>
          {item.normalized_address && (
            <p className="review-address">
              <span className="muted">Normalisiert:</span> {item.normalized_address}
            </p>
          )}
        </div>
        <div className="review-status">
          {assumed ? <span className="badge badge-warn">{assumedLabel}</span> : <GeoStatusBadge status={item.geo_status} />}
          {item.geo_confidence != null && <small className="muted">Sicherheit {Math.round(item.geo_confidence * 100)} %</small>}
        </div>
      </div>
      <p className="review-problem">{details.message ?? geoStatusLabels[item.geo_status]}</p>
      {!!details.problems?.length && (
        <ul className="tags" aria-label="Erkannte Probleme">
          {details.problems.map((p) => (
            <li key={p} className="tag">
              {problemLabels[p] ?? p}
            </li>
          ))}
        </ul>
      )}
      {details.resolved && (
        <p className="muted">Erkannt: {[details.resolved.municipality, details.resolved.county, details.resolved.federal_state].filter(Boolean).join(' · ')}</p>
      )}
      {!save.isSuccess && <AddressCheckSuggestion item={item} />}
      {save.isSuccess ? (
        <p className="alert alert-success" role="status">
          Gespeichert – neuer Status: {geoStatusLabels[save.data.geo_status]}.
        </p>
      ) : (
        <button type="button" className="btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? 'Schließen' : assumed ? 'Prüfen' : 'Klären'}
        </button>
      )}
      {open && !save.isSuccess && (
        <form className="review-form" onSubmit={submit({ mark_reviewed: true })} aria-label={`Klärung für ${name(item)}`}>
          {!!details.candidates?.length && (
            <div className="candidates">
              <h3>Vorschläge</h3>
              <ul>
                {details.candidates.map((c, i) => (
                  <li key={`${c.postal_code}-${c.city}-${i}`}>
                    <span>
                      <strong>{c.postal_code || '–'}</strong> {c.city}
                      {c.county_name && <span className="muted"> · {c.county_name}</span>}
                      <span className="muted"> ({c.source === 'CITY' ? 'aus Ortsname' : 'aus PLZ'})</span>
                    </span>
                    <button
                      type="button"
                      className="btn btn-small"
                      onClick={() => setForm((f) => ({ ...f, postal_code: c.postal_code || f.postal_code, city: c.city || f.city }))}
                    >
                      Übernehmen
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="form-grid">
            <AddressAutocomplete
              label="Straße"
              value={form.street}
              onChange={(street) => setForm((f) => ({ ...f, street }))}
              context={[form.postal_code, form.city].filter(Boolean).join(' ')}
              onPick={(a) =>
                setForm((f) => ({
                  ...f,
                  street: a.street || f.street,
                  house_number: a.house_number || f.house_number,
                  postal_code: a.postal_code,
                  city: a.city,
                }))
              }
            />
            <label className="field narrow">
              <span>Hausnr.</span>
              <input value={form.house_number} onChange={(e) => setForm({ ...form, house_number: e.target.value })} />
            </label>
            <label className="field narrow">
              <span>PLZ</span>
              <input inputMode="numeric" maxLength={5} value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
            </label>
            <label className="field">
              <span>Ort</span>
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </label>
            <div className="field wide">
              <AddressCheck address={form} onApply={(a) => setForm((f) => ({ ...f, ...a }))} />
            </div>
            {canOverride && counties.size > 0 && (
              <label className="field">
                <span>Landkreis festlegen</span>
                <select value={countyId} onChange={(e) => setCountyId(e.target.value)}>
                  <option value="">Automatisch bestimmen</option>
                  {[...counties].map(([id, label]) => (
                    <option key={id} value={id}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {canOverride && areas.length > 0 && (
              <label className="field">
                <span>Gebiet festlegen</span>
                <select value={areaId} onChange={(e) => setAreaId(e.target.value)}>
                  <option value="">Automatisch bestimmen</option>
                  {areas.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                  <option value="none">Kein Gebiet</option>
                </select>
              </label>
            )}
            <label className="field wide">
              <span>Notiz</span>
              <input value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} placeholder="z. B. Rückfrage beim Kunden" />
            </label>
          </div>
          {save.isError && <MutationError error={save.error} />}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>
              {assumed ? 'Bestätigen und als geprüft markieren' : 'Speichern und als geprüft markieren'}
            </button>
            <button type="button" className="btn" disabled={save.isPending} onClick={submit({})}>
              Nur speichern
            </button>
            <button type="button" className="btn btn-ghost" disabled={save.isPending} onClick={submit({ request_manual_review: true })}>
              Zur manuellen Prüfung
            </button>
          </div>
        </form>
      )}
    </li>
  );
}
